import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  BookOpen,
  LayoutGrid,
  Landmark,
  Target,
  Sparkles,
  Menu,
  ChevronRight,
  User,
  Users,
  LogOut,
  Shield,
  Eye,
  EyeOff,
  ClipboardList,
  Heart,
  Smartphone,
  Package,
  Coins,
  Settings,
  Barcode
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CirculateLogo, CirculateLogoMini } from "@/components/CirculateLogo";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/proposals", label: "Program Proposals", icon: FileText },
  { path: "/events", label: "Events", icon: Calendar },
  { path: "/inventory", label: "Inventory", icon: Package },
  { path: "/offline-checkout", label: "Offline Checkout", icon: Barcode },
  { path: "/books", label: "Books", icon: BookOpen },
  { path: "/book-displays", label: "Book Displays", icon: LayoutGrid },
  { path: "/literacy-stations", label: "Literacy Stations", icon: Landmark },
  { path: "/yearly-goals", label: "Yearly Goals", icon: Target },
  { path: "/shout-outs", label: "Shout Outs", icon: Heart },
  { path: "/ai-assistant", label: "AI Assistant", icon: Sparkles },
];

export const Layout = ({ children, user, onLogout, viewMode, setViewMode, onSwitchToMobile, isMobile }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  const isSupervisor = user?.role === "supervisor";
  const isAdmin = user?.is_admin === true;
  const canToggleView = isSupervisor || isAdmin;
  const isStaffView = viewMode === "staff";
  
  // Get user's feature flags (admin has all flags enabled)
  const featureFlags = isAdmin 
    ? Object.fromEntries(Object.keys(user?.feature_flags || {}).map(k => [k, true]))
    : (user?.feature_flags || {});
  
  // Check if a feature is enabled
  const hasFeature = (feature) => isAdmin || featureFlags[feature] === true;

  // Filter nav items based on feature flags
  const filteredNavItems = navItems.filter(item => {
    const featureMap = {
      "/proposals": "proposals",
      "/events": "events",
      "/inventory": "inventory",
      "/books": "books",
      "/book-displays": "book_displays",
      "/literacy-stations": "literacy_stations",
      "/yearly-goals": "yearly_goals",
      "/shout-outs": "shout_outs",
      "/ai-assistant": "ai_assistant",
      "/supplies": "supply_inventory",
      "/offline-checkout": "offline_checkout",
    };
    
    // Dashboard is always visible
    if (item.path === "/") return true;
    
    const requiredFeature = featureMap[item.path];
    if (!requiredFeature) return true; // No feature flag required
    
    return hasFeature(requiredFeature);
  });

  return (
    <div className="flex min-h-screen bg-brand-cream">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen sidebar-brand border-r border-stone-200 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20"
        )}
        data-testid="sidebar"
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-stone-200">
            {sidebarOpen ? (
              <CirculateLogo size={36} showText={true} data-testid="app-title" />
            ) : (
              <CirculateLogoMini size={32} />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle"
              className="ml-auto"
            >
              {sidebarOpen ? <ChevronRight className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
              
              {/* Supervisor/Admin only: User Management */}
              {(isSupervisor || isAdmin) && hasFeature("user_management") && (
                <NavLink
                  to="/users"
                  data-testid="nav-users"
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    location.pathname === "/users"
                      ? "bg-purple-600 text-white"
                      : "text-purple-600 hover:bg-purple-50"
                  )}
                >
                  <Users className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {sidebarOpen && <span>User Management</span>}
                </NavLink>
              )}
              
              {/* Supervisor/Admin only: Supervisor Dashboard */}
              {(isSupervisor || isAdmin) && hasFeature("supervisor_dashboard") && (
                <NavLink
                  to="/supervisor"
                  data-testid="nav-supervisor"
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    location.pathname === "/supervisor"
                      ? "bg-amber-600 text-white"
                      : "text-amber-600 hover:bg-amber-50"
                  )}
                >
                  <ClipboardList className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {sidebarOpen && <span>Supervisor Dashboard</span>}
                </NavLink>
              )}
              
              {/* Admin only: Feature Flags Admin */}
              {isAdmin && (
                <NavLink
                  to="/admin/feature-flags"
                  data-testid="nav-feature-flags"
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    location.pathname === "/admin/feature-flags"
                      ? "bg-red-600 text-white"
                      : "text-red-600 hover:bg-red-50"
                  )}
                >
                  <Settings className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {sidebarOpen && <span>Feature Flags</span>}
                </NavLink>
              )}
            </nav>
          </ScrollArea>

          {/* User Info & Logout */}
          <div className="border-t border-stone-200 p-4 space-y-3">
            {/* Mobile View Switch */}
            {onSwitchToMobile && sidebarOpen && (
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={onSwitchToMobile}
                data-testid="switch-to-mobile-btn"
              >
                <Smartphone className="h-4 w-4" />
                Simple View
              </Button>
            )}
            {onSwitchToMobile && !sidebarOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onSwitchToMobile}
                className="w-full text-blue-600"
                title="Switch to Simple View"
                data-testid="switch-to-mobile-btn-collapsed"
              >
                <Smartphone className="h-5 w-5" />
              </Button>
            )}
            
            {/* View Mode Toggle for Admins and Supervisors */}
            {canToggleView && sidebarOpen && (
              <div className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {isStaffView ? (
                    <EyeOff className="h-4 w-4 text-amber-600" />
                  ) : (
                    <Eye className="h-4 w-4 text-purple-600" />
                  )}
                  <Label htmlFor="view-toggle" className="text-xs font-medium cursor-pointer">
                    {isStaffView ? "My Data Only" : "All Data"}
                  </Label>
                </div>
                <Switch
                  id="view-toggle"
                  checked={!isStaffView}
                  onCheckedChange={(checked) => setViewMode(checked ? "default" : "staff")}
                  data-testid="view-mode-toggle"
                />
              </div>
            )}
            {canToggleView && !sidebarOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode(isStaffView ? "default" : "staff")}
                className={cn(
                  "w-full",
                  isStaffView ? "text-amber-600" : "text-purple-600"
                )}
                title={isStaffView ? "Viewing: My Data Only" : "Viewing: All Data"}
                data-testid="view-mode-toggle-collapsed"
              >
                {isStaffView ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start gap-2",
                    !sidebarOpen && "justify-center px-2"
                  )}
                  data-testid="user-menu"
                >
                  {isAdmin ? (
                    <Shield className="h-4 w-4 text-amber-600" />
                  ) : isSupervisor ? (
                    <Shield className="h-4 w-4 text-purple-600" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  {sidebarOpen && (
                    <div className="flex flex-col items-start text-left">
                      <span className="text-sm font-medium">{user?.name}</span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {isAdmin ? "Admin" : user?.role}
                      </span>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <div className="flex gap-1 mt-1">
                    {isAdmin && (
                      <Badge className="text-xs bg-amber-100 text-amber-700">
                        Admin
                      </Badge>
                    )}
                    <Badge 
                      className={cn(
                        "text-xs",
                        isSupervisor ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                      )}
                    >
                      {user?.role}
                    </Badge>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  className="text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "flex-1 transition-all duration-300",
          sidebarOpen ? "ml-64" : "ml-20"
        )}
      >
        {/* Rainbow accent bar */}
        <div className="h-1 brand-gradient" />
        <div className="min-h-screen bg-background p-6 md:p-10">
          {children}
        </div>
      </main>
    </div>
  );
};
